import {
  Component,
  OnInit,
  Input,
  HostBinding,
  ViewChild,
  ElementRef,
  HostListener,
  ViewChildren,
} from "@angular/core";
import { IPictureData } from "src/app/shared/components/picture/picture.component";
import { IUProperty } from "src/app/shared/interface/umbraco-property";
import { SafeHtml } from "@angular/platform-browser";
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from "@angular/animations";
import { applyTitleSettings, applySubtitleSettings } from '../../shared/utils/title-style-settings-utils';
import { ITitleStyleSettings, ISubtitleStyleSettings } from '../../shared/interface/title-style-settings';
import { IRteStyleSettings } from "../../shared/interface/rte-style-settings";
import { IItemSettings } from "../../shared/interface/item-settings";
import { applyItemSettings } from "../../shared/utils/item-settings-utils";
import { applyRteSettings } from "../../shared/utils/rte-style-settings-utils";

interface IProfilesPanelData {
  title?: IUProperty<string>;
  subtitle?: IUProperty<string>;
  capitalizeTitle?: IUProperty<string[]>;
  items?: IUProperty<IProfileItem[]>;
  itemsJobTitleSize: IUProperty<string>;
  itemsJobTitleFontWeight: IUProperty<string>;
  backgroundImage?: IUProperty<IPictureData>;
  bottomText: IUProperty<string>;
  topWave?: IUProperty<boolean>;
  bottomWave?: IUProperty<boolean>;

  rteStyleSettingsComposition?: IRteStyleSettings;
  titleStyleSettingsComposition?: ITitleStyleSettings;
  subtitleStyleSettingsComposition?: ISubtitleStyleSettings;
  itemSettingsComposition?: IItemSettings;
}

interface IProfileItem {
  author: IUProperty<string>;
  description: IUProperty<string | SafeHtml>;
  image: IUProperty<IPictureData>;
  position: IUProperty<string>;
  state?: string;
  nameColor: IUProperty<string>;
  jobTitleColor: IUProperty<string>;
  descriptionColor: IUProperty<string>;
  roundedCorners: IUProperty<string>;
  backgroundColor: IUProperty<string>;
}

enum ProfilesItemState {
  opened = "opened",
  closed = "closed",
}

@Component({
  selector: "app-profiles-panel",
  templateUrl: "./profiles-panel.component.html",
  styleUrls: ["./profiles-panel.component.less"],
  animations: [
    trigger("animation", [
      state(
        "opened",
        style({
          height: "*",
          opacity: "1",
        })
      ),
      state(
        "closed",
        style({
          height: "0",
          opacity: "0",
          paddingBottom: "0",
          paddingTop: "0",
        })
      ),
      transition("* => *", [animate(".2s")]),
    ]),
    trigger("arrow", [
      state(
        "opened",
        style({
          transform: "rotate(180deg)",
          marginTop: "45px",
        })
      ),
      state(
        "closed",
        style({
          transform: "rotate(0deg)",
          marginTop: "0px",
        })
      ),
      transition("* => *", [animate(".2s")]),
    ]),
  ],
})
export class ProfilesPanelComponent implements OnInit {
  @Input() data: IProfilesPanelData;
  profilesItemState = ProfilesItemState;
  scrollHeight: number = null;
  itemState: string;
  itemActive = false;
  items: any[] = [];

  @HostBinding("class") hostClass = "";
  @ViewChild("panelEl", { static: false }) panelElRef: ElementRef<HTMLElement>;
  @ViewChild("titleEl", { static: false }) titleEl: ElementRef<HTMLElement>;
  @ViewChild("profileEl", { static: false }) profileEl: ElementRef<HTMLElement>;
  @ViewChild("bottomTextEl", { static: false }) bottomTextEl: ElementRef<HTMLElement>;
  @ViewChild("subtitleEl", { static: false }) subtitleEl: ElementRef<HTMLElement>;
  @ViewChildren("profilesEl") profilesEl;
  @ViewChildren("imageEl") imageEl;

  @HostListener("window:resize", ["$event"]) onResize() {
    this.closeAllItems();
  }

  @HostListener("window:scroll", []) onWindowScroll() {
    this.scrollHeight = window.pageYOffset;
    if (
      this.scrollHeight + window.outerHeight >
      this.panelElRef.nativeElement.offsetTop
    ) {
      this.loadMedia();
    }
  }

  ngOnInit() {
    this.scrollHeight = window.pageYOffset;

    this.items = this.data.items.value;

    if (this.data && this.data.topWave && this.data.topWave.value) {
      this.hostClass += " profiles-panel--top-wave";
    }
    if (this.data && this.data.bottomWave && this.data.bottomWave.value) {
      this.hostClass += " profiles-panel--bottom-wave";
    }

    this.closeAllItems();
  }

  ngAfterViewInit(): void {
    this.applyCustomItemStyling();
    this.applyCustomPanelStyling();

    if (
      this.scrollHeight + window.outerHeight + 50 >
      this.panelElRef.nativeElement.offsetTop
    ) {
      this.loadMedia();
    }
  }

  private applyCustomPanelStyling() {
    applyTitleSettings(this.data, this.titleEl);
    applySubtitleSettings(this.data, this.subtitleEl);
    applyRteSettings(this.data, this.bottomTextEl);

    if (
      this.data &&
      this.data.capitalizeTitle &&
      this.data.capitalizeTitle.value
    ) {
      this.data.capitalizeTitle.value.forEach((item) => {
        switch (item) {
          case "Title":
            if (this.titleEl && this.titleEl.nativeElement)
              this.titleEl.nativeElement.style.textTransform = "uppercase";
            break;
          case "Subtitle":
            if (this.subtitleEl && this.subtitleEl.nativeElement)
              this.subtitleEl.nativeElement.style.textTransform = "uppercase";
        }
      });
    }
  }

  private applyCustomItemStyling() {
    applyItemSettings(this.data, this.profileEl)

    if (this.data.itemsJobTitleSize && this.data.itemsJobTitleSize.value) {
      this.profileEl.nativeElement.style.setProperty("--card-job-title-size",
        this.data.itemsJobTitleSize.value + 'px')
    }
    if (this.data.itemsJobTitleFontWeight && this.data.itemsJobTitleFontWeight.value) {
      this.profileEl.nativeElement.style.setProperty("--card-job-title-font-weight",
        this.data.itemsJobTitleFontWeight.value)
    }

    if (this.profilesEl) {
      this.profilesEl.forEach((item, i) => {
        const currentElement = this.data.items.value[i];

      if (currentElement.nameColor.value && currentElement) {
        item.nativeElement.style.setProperty(
          "--card-name-color",
          currentElement.nameColor.value
        );
      }

      if (currentElement.jobTitleColor.value && currentElement) {
        item.nativeElement.style.setProperty(
          "--card-job-title-color",
          currentElement.jobTitleColor.value
        );
      }

      if (currentElement.descriptionColor.value && currentElement) {
        item.nativeElement.style.setProperty(
          "--card-description-color",
          currentElement.descriptionColor.value
        );
      }

      if (currentElement.backgroundColor.value && currentElement) {
        item.nativeElement.style.setProperty(
          "--card-background-color",
          currentElement.backgroundColor.value
        );
      }

      if (currentElement.roundedCorners.value && currentElement) {
        if (this.data.itemSettingsComposition &&
          this.data.itemSettingsComposition.itemBorderRadius &&
          this.data.itemSettingsComposition.itemBorderRadius.value) {
          item.nativeElement.style.setProperty("--card-border-radius",
            this.data.itemSettingsComposition.itemBorderRadius.value + 'px');
        } else { 
          item.nativeElement.style.setProperty("--card-border-radius", `5px`);
        }
      }
    });
  }
}

  loadMedia() {
    if (this.panelElRef && this.panelElRef.nativeElement) {
      if (
        this.data &&
        this.data.backgroundImage &&
        this.data.backgroundImage.value
      ) {
        this.panelElRef.nativeElement.style.backgroundImage = `url(${this.data.backgroundImage.value.src})`;
        this.panelElRef.nativeElement.style.backgroundSize = "cover";
        this.panelElRef.nativeElement.style.backgroundRepeat = "no-repeat";
      }
    }

    this.imageEl.forEach((image, i) => {
      image.nativeElement.style.backgroundImage =
        "url(" + this.items[i].image.value.src + ")";
    });
  }

  closeAllItems() {
    this.data.items.value = this.data.items.value.map((i) => {
      i.state = ProfilesItemState.closed;

      return i;
    });
  }

  toggleItemState(item: any) {
    if (item.state === ProfilesItemState.opened) {
      item.state = this.profilesItemState.closed;
    } else {
      item.state = this.profilesItemState.opened;
    }
  }
}
